import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, Globe } from "lucide-react";
import useScrollReveal from "../../hooks/useScrollReveal";
import Nav from "../site/Nav";
import SEO from "../site/SEO";
import Footer from "../site/Footer";
import FinalCta from "../site/FinalCta";
import StickyMobileCTA from "../site/StickyMobileCTA";
import MethodSection from "../site/MethodSection";
import { ProcessSection, FAQSection } from "../site/OfferingPrimitives";
import {
  WEBSITE_PACKAGES,
  LANDING_PAGE_PACKAGE,
  toWebsitesCardShape,
} from "../../data/websitePackages";

const websiteFaqs = [
  {
    q: "How long does a website build take?",
    a: "Starter Website: 7–10 days. Growth Website: 2–3 weeks. Premium Website: 2–4 weeks. Landing Page Build: 5–7 days. Timelines depend on how quickly content, brand assets, and access are provided.",
  },
  {
    q: "What do I need to provide to start?",
    a: "A short business overview, your offer/services, any existing copy or photos, a logo if you have one, and access to your domain registrar when we're ready to launch. The Build Brief intake walks you through everything step by step.",
  },
  {
    q: "Do you write the copy?",
    a: "We help structure it. You provide the raw service descriptions and positioning; we tighten it into clean, conversion-ready website copy. Full ground-up copywriting can be added if you don't have anything written yet.",
  },
  {
    q: "Will my website be mobile-friendly?",
    a: "Every Modern Exit website is mobile-first. We design for phone screens before desktop, then test across breakpoints before launch.",
  },
  {
    q: "Can I add a revenue system later?",
    a: "Yes. Website packages can be upgraded later with lead capture automation, CRM setup, booking flow, follow-up sequences, and reactivation systems via the Revenue Systems path.",
  },
];

const PACKAGES = WEBSITE_PACKAGES.map(toWebsitesCardShape);
const LANDING_PAGE = toWebsitesCardShape(LANDING_PAGE_PACKAGE);

const WHAT_WE_BUILD = [
  "One-page websites",
  "Landing pages",
  "Full business websites",
  "Service-based websites",
  "Mobile-first websites",
  "Lead capture forms",
  "Booking link integration",
  "Basic SEO structure",
  "Launch-ready web presence",
];

const openAudit = () => window.dispatchEvent(new Event("open-audit"));

export default function WebsitesPage() {
  useScrollReveal();
  const navigate = useNavigate();
  const goToBuildBrief = () => navigate("/build-brief");

  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="websites-page">
      <SEO page="/websites" />
      <Nav />
      <main className="pt-24 sm:pt-28 md:pt-32">
        {/* Hero */}
        <section className="px-5 sm:px-6 md:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
              <div className="lg:col-span-8 reveal">
                <p className="eyebrow inline-flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-clay" />
                  Modern Exit Websites
                </p>
                <h1
                  data-kinetic
                  data-testid="websites-headline"
                  className="font-display mt-5 text-[2.1rem] sm:text-[2.6rem] md:text-5xl lg:text-[3.6rem] xl:text-[4rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink"
                >
                  Professional websites built to make service businesses{" "}
                  <span className="italic text-ink-muted">look credible,</span>{" "}
                  capture leads, and move visitors to action.
                </h1>
              </div>
              <div className="lg:col-span-4 reveal">
                <p className="text-base md:text-lg text-ink-muted leading-relaxed">
                  We build clean, mobile-first websites and landing pages that
                  explain your offer, build trust, capture leads, and give
                  prospects a clear next step toward booking, calling, or
                  buying.
                </p>
              </div>
            </div>

            <div className="mt-8 sm:mt-10 reveal flex flex-col sm:flex-row gap-3">
              <Link
                to="/build-brief"
                data-testid="websites-primary-cta"
                data-magnetic
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-ink-deep text-sand-100 px-7 py-4 text-[15px] font-medium hover:bg-rust transition-colors min-h-[52px]"
              >
                Start My Website Build
                <ArrowRight size={16} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Our Method */}
        <MethodSection />

        {/* What we build */}
        <section className="py-20 sm:py-24 md:py-28">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
              <div className="lg:col-span-7 reveal">
                <p className="eyebrow !text-clay">What We Build</p>
                <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
                  Clean websites.
                  <br />
                  <span className="italic text-ink-muted">Built to convert.</span>
                </h2>
              </div>
              <div className="lg:col-span-5 reveal">
                <p className="text-base md:text-lg text-ink-muted leading-relaxed">
                  Every Modern Exit Websites build is shipped as a complete
                  launch — not a half-finished template. Mobile-first, fast,
                  and structured to capture leads from day one.
                </p>
              </div>
            </div>

            <div className="reveal grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {WHAT_WE_BUILD.map((item, i) => (
                <div
                  key={item}
                  data-testid={`websites-build-item-${i}`}
                  className="group rounded-2xl border border-sand-300 bg-sand-50 p-5 sm:p-6 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-26px_rgba(17,16,14,0.22)] transition-all flex items-start gap-3"
                >
                  <span className="mt-0.5 h-8 w-8 grid place-items-center rounded-xl bg-sand-100 border border-sand-300 text-ink shrink-0">
                    <Globe size={14} strokeWidth={1.6} />
                  </span>
                  <span className="text-[14px] text-ink leading-snug">
                    {item}
                  </span>
                </div>
              ))}
            </div>

            {/* What we don't do */}
            <div className="mt-12 reveal rounded-2xl border border-sand-300 bg-sand-50 p-6 sm:p-7 md:p-9 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <p className="font-display text-lg sm:text-xl md:text-2xl text-ink leading-snug max-w-3xl">
                We build new, modern websites{" "}
                <span className="text-ink-muted">
                  designed to launch cleanly and capture leads from day one.
                </span>
              </p>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-clay shrink-0">
                · no patchwork refreshes
              </span>
            </div>
          </div>
        </section>

        {/* Packages */}
        <section className="pb-20 sm:pb-24 md:pb-28 lg:pb-32" id="packages">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
            <div className="grid lg:grid-cols-12 gap-10 items-end mb-10 sm:mb-14">
              <div className="lg:col-span-7 reveal">
                <p className="eyebrow">Website Packages</p>
                <h2 className="font-display mt-5 text-3xl sm:text-4xl md:text-5xl lg:text-[3.2rem] leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
                  Four ways to launch.
                  <br />
                  <span className="italic text-ink-muted">Pick the one that fits.</span>
                </h2>
              </div>
              <div className="lg:col-span-5 reveal">
                <p className="text-base md:text-lg text-ink-muted leading-relaxed">
                  No long retainers. No bloated agency packages. Just a clean
                  build, shipped fast, ready to capture leads.
                </p>
              </div>
            </div>

            {/* Add-on · Campaign Page — sits above the 3-card main row */}
            <div className="reveal text-center mb-6 sm:mb-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-clay">
                Add-on · Campaign Page
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5 mb-12 sm:mb-16">
              <div className="hidden md:block" aria-hidden="true" />
              <PackageCard pkg={LANDING_PAGE} onCta={goToBuildBrief} />
              <div className="hidden md:block" aria-hidden="true" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
              {PACKAGES.map((p) => (
                <PackageCard pkg={p} key={p.id} onCta={goToBuildBrief} />
              ))}
            </div>
          </div>
        </section>

        <ProcessSection
          eyebrow="How It Works"
          title="Simple build, clean launch."
        />

        <FAQSection faqs={websiteFaqs} />

        <FinalCta
          eyebrow="Launch The Modern Exit Way"
          headline="Ready to launch a"
          italicTail="website that works?"
          body="Tell us about your business. We'll match you to the right Website package and send the next step."
          primaryLabel="Start My Build Brief"
          onPrimary={goToBuildBrief}
        />
      </main>
      <Footer />
      <StickyMobileCTA
        label="Start My Website"
        onClick={goToBuildBrief}
        testId="websites-sticky-cta"
      />
    </div>
  );
}

function PackageCard({ pkg, onCta }) {
  const emp = pkg.emphasized;
  return (
    <article
      data-testid={`website-package-${pkg.id}`}
      className={`reveal relative h-full rounded-3xl border p-7 sm:p-8 flex flex-col transition-all ${
        emp
          ? "bg-sand-50 border-clay/60 shadow-[0_30px_70px_-30px_rgba(165,106,67,0.35)] md:scale-[1.02]"
          : "bg-sand-50 border-sand-300 md:hover:-translate-y-0.5"
      }`}
    >
      {pkg.badge && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 rounded-full bg-clay text-sand-100 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] whitespace-nowrap">
          <span className="h-1.5 w-1.5 rounded-full bg-sand-100" />
          {pkg.badge}
        </span>
      )}
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg md:text-xl font-medium tracking-tight text-ink">
          {pkg.name}
        </h3>
      </div>
      <div className="mt-5 flex items-baseline gap-2 flex-wrap">
        <span className="font-display text-[2.4rem] sm:text-[2.6rem] md:text-[2.8rem] font-semibold tracking-[-0.02em] text-ink leading-none">
          {pkg.price}
        </span>
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-muted">
          one-time
        </span>
      </div>
      <p className="mt-3 text-[14px] text-ink-muted leading-relaxed">
        {pkg.bestFor}
      </p>
      <ul className="mt-6 space-y-2.5 border-t border-sand-300 pt-6 flex-1">
        {pkg.features.map((f) => (
          <li
            key={f}
            className="flex items-start gap-3 text-[14px] text-ink leading-relaxed"
          >
            <span
              className={`mt-0.5 h-5 w-5 grid place-items-center rounded-full ${
                emp ? "bg-clay/15 text-clay" : "bg-sand-200 text-olive"
              }`}
            >
              <Check size={12} strokeWidth={2.4} />
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>

      {pkg.seo && (
        <div
          data-testid={`${pkg.id}-seo`}
          className="mt-5 rounded-2xl border border-sand-300 bg-sand-100/70 px-4 py-4"
        >
          <p className="font-mono text-[9.5px] uppercase tracking-[0.28em] text-clay">
            {pkg.seo.title}
          </p>
          <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-muted">
            {pkg.seo.description}
          </p>
          <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5 text-[12.5px] leading-snug text-ink">
            {pkg.seo.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2">
                <span
                  className={`mt-[5px] h-1 w-1 rounded-full shrink-0 ${
                    emp ? "bg-clay" : "bg-olive"
                  }`}
                />
                <span>{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <button
        type="button"
        onClick={onCta}
        data-testid={`website-package-cta-${pkg.id}`}
        className={`mt-7 group inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 sm:py-3.5 text-[14px] font-medium transition-colors min-h-[52px] sm:min-h-0 ${
          emp
            ? "bg-ink-deep text-sand-100 hover:bg-ink"
            : "border border-ink-deep text-ink hover:bg-sand-200"
        }`}
      >
        {pkg.cta}
        <ArrowRight size={15} className="transition-transform group-hover:translate-x-0.5" />
      </button>
    </article>
  );
}
